import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    MessageCircle,
    Send,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Users,
    FileText,
    Plus,
    Search,
    Filter,
    Loader2,
    Phone,
    Calendar,
    MoreVertical,
    RefreshCw,
    Zap,
    MessageSquare,
    ChevronDown,
    X,
    Check,
} from 'lucide-react';

interface Message {
    id: string;
    recipient_phone: string;
    recipient_name: string | null;
    template_name: string | null;
    message_body: string | null;
    status: string;
    sent_at: string | null;
    delivered_at: string | null;
    read_at: string | null;
    error_message: string | null;
    created_at: string;
}

interface Template {
    id: string;
    name: string;
    category: string;
    language: string;
    body_text: string;
    is_active: boolean;
}

interface QuotaInfo {
    used: number;
    limit: number;
    resetsAt: string;
}

interface Student {
    id: string;
    full_name: string;
    phone: string | null;
}

export function WhatsAppPage() {
    const { tenant, profile } = useAuth();
    const queryClient = useQueryClient();
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSendModal, setShowSendModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
    const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
    const [customMessage, setCustomMessage] = useState('');

    // Fetch quota info
    const { data: quota } = useQuery({
        queryKey: ['whatsapp-quota', tenant?.id],
        queryFn: async (): Promise<QuotaInfo> => {
            if (!tenant?.id) return { used: 0, limit: 100, resetsAt: '' };

            const now = new Date();
            const period = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

            const { data } = await supabase
                .from('whatsapp_quotas')
                .select('*')
                .eq('tenant_id', tenant.id)
                .eq('period', period)
                .single();

            return {
                used: data?.used || 0,
                limit: data?.included_limit || 100,
                resetsAt: new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString(),
            };
        },
        enabled: !!tenant?.id,
    });

    // Fetch messages
    const { data: messages, isLoading: isLoadingMessages } = useQuery({
        queryKey: ['whatsapp-messages', tenant?.id, statusFilter],
        queryFn: async () => {
            if (!tenant?.id) return [];

            let query = supabase
                .from('whatsapp_messages')
                .select('*')
                .eq('tenant_id', tenant.id)
                .order('created_at', { ascending: false })
                .limit(100);

            if (statusFilter) {
                query = query.eq('status', statusFilter);
            }

            const { data, error } = await query;
            if (error) throw error;
            return data as Message[];
        },
        enabled: !!tenant?.id,
    });

    // Fetch templates
    const { data: templates } = useQuery({
        queryKey: ['whatsapp-templates'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('whatsapp_templates')
                .select('*')
                .eq('is_active', true)
                .order('name');

            if (error) throw error;
            return data as Template[];
        },
    });

    // Fetch students for sending
    const { data: students } = useQuery({
        queryKey: ['students-whatsapp', tenant?.id],
        queryFn: async () => {
            if (!tenant?.id) return [];

            const { data, error } = await supabase
                .from('student_pairings')
                .select(`
                    student:student_id (
                        id,
                        full_name,
                        phone
                    )
                `)
                .eq('tenant_id', tenant.id)
                .eq('status', 'active');

            if (error) throw error;

            return (data || [])
                .map((p) => p.student as unknown as Student)
                .filter((s) => s && s.phone);
        },
        enabled: !!tenant?.id,
    });

    // Send message mutation
    const sendMessageMutation = useMutation({
        mutationFn: async () => {
            if (!tenant?.id || !profile?.id) return;

            const messagesToSend = selectedStudents.map((studentId) => {
                const student = students?.find((s) => s.id === studentId);
                return {
                    tenant_id: tenant.id,
                    sender_id: profile.id,
                    recipient_phone: student?.phone,
                    recipient_name: student?.full_name,
                    template_name: selectedTemplate?.name || null,
                    message_body: customMessage || selectedTemplate?.body_text || null,
                    status: 'pending',
                };
            });

            const { error } = await supabase
                .from('whatsapp_messages')
                .insert(messagesToSend);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['whatsapp-messages'] });
            queryClient.invalidateQueries({ queryKey: ['whatsapp-quota'] });
            setShowSendModal(false);
            setSelectedStudents([]);
            setSelectedTemplate(null);
            setCustomMessage('');
        },
    });

    // Filter messages
    const filteredMessages = messages?.filter((msg) =>
        msg.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        msg.recipient_phone?.includes(searchTerm) ||
        msg.template_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'sent':
                return <Check className="w-4 h-4 text-blue-500" />;
            case 'delivered':
                return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'read':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'failed':
                return <XCircle className="w-4 h-4 text-red-500" />;
            case 'pending':
            default:
                return <Clock className="w-4 h-4 text-yellow-500" />;
        }
    };

    const getStatusLabel = (status: string) => {
        const labels: Record<string, string> = {
            pending: 'Aguardando',
            sent: 'Enviado',
            delivered: 'Entregue',
            read: 'Lido',
            failed: 'Falhou',
        };
        return labels[status] || status;
    };

    const quotaPercentage = quota ? (quota.used / quota.limit) * 100 : 0;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="section-title flex items-center gap-2">
                        <MessageCircle className="w-6 h-6" />
                        WhatsApp
                        <span className="badge badge-primary">PRO</span>
                    </h1>
                    <p className="section-subtitle">
                        Envie mensagens automáticas para seus alunos
                    </p>
                </div>
                <button
                    onClick={() => setShowSendModal(true)}
                    className="btn-primary"
                >
                    <Send className="w-4 h-4 mr-2" />
                    Nova Mensagem
                </button>
            </div>

            {/* Quota Card */}
            <div className="card bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-600 rounded-lg">
                            <Zap className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900">Cota de Mensagens</h3>
                            <p className="text-sm text-gray-600">
                                Renova em {quota?.resetsAt ? new Date(quota.resetsAt).toLocaleDateString('pt-BR') : 'N/A'}
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">
                            {quota?.used || 0} / {quota?.limit || 100}
                        </p>
                        <p className="text-sm text-gray-500">mensagens usadas</p>
                    </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                        className={`h-2 rounded-full transition-all ${quotaPercentage > 80 ? 'bg-red-500' : quotaPercentage > 50 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                        style={{ width: `${Math.min(quotaPercentage, 100)}%` }}
                    />
                </div>
            </div>

            {/* Stats */}
            <div className="grid gap-4 md:grid-cols-4">
                <StatCard
                    icon={<Send className="w-5 h-5" />}
                    label="Enviadas"
                    value={messages?.filter((m) => m.status === 'sent').length || 0}
                    color="blue"
                />
                <StatCard
                    icon={<CheckCircle className="w-5 h-5" />}
                    label="Entregues"
                    value={messages?.filter((m) => m.status === 'delivered').length || 0}
                    color="green"
                />
                <StatCard
                    icon={<MessageSquare className="w-5 h-5" />}
                    label="Lidas"
                    value={messages?.filter((m) => m.status === 'read').length || 0}
                    color="purple"
                />
                <StatCard
                    icon={<XCircle className="w-5 h-5" />}
                    label="Falharam"
                    value={messages?.filter((m) => m.status === 'failed').length || 0}
                    color="red"
                />
            </div>

            {/* Messages Table */}
            <div className="card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <h2 className="font-semibold flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Histórico de Mensagens
                    </h2>
                    <div className="flex gap-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input pl-9 py-2 text-sm w-48"
                            />
                        </div>
                        <select
                            value={statusFilter || ''}
                            onChange={(e) => setStatusFilter(e.target.value || null)}
                            className="input py-2 text-sm"
                        >
                            <option value="">Todos Status</option>
                            <option value="pending">Aguardando</option>
                            <option value="sent">Enviado</option>
                            <option value="delivered">Entregue</option>
                            <option value="read">Lido</option>
                            <option value="failed">Falhou</option>
                        </select>
                    </div>
                </div>

                {isLoadingMessages ? (
                    <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                    </div>
                ) : filteredMessages && filteredMessages.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b text-left text-sm text-gray-500">
                                    <th className="pb-3 font-medium">Destinatário</th>
                                    <th className="pb-3 font-medium">Template/Mensagem</th>
                                    <th className="pb-3 font-medium">Status</th>
                                    <th className="pb-3 font-medium">Enviado em</th>
                                    <th className="pb-3 font-medium"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {filteredMessages.map((message) => (
                                    <tr key={message.id} className="hover:bg-gray-50">
                                        <td className="py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                                    <Phone className="w-4 h-4 text-green-600" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">
                                                        {message.recipient_name || 'N/A'}
                                                    </p>
                                                    <p className="text-sm text-gray-500">
                                                        {message.recipient_phone}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4">
                                            {message.template_name ? (
                                                <span className="badge badge-secondary">
                                                    {message.template_name}
                                                </span>
                                            ) : (
                                                <p className="text-sm text-gray-600 max-w-[200px] truncate">
                                                    {message.message_body}
                                                </p>
                                            )}
                                        </td>
                                        <td className="py-4">
                                            <div className="flex items-center gap-2">
                                                {getStatusIcon(message.status)}
                                                <span className="text-sm">
                                                    {getStatusLabel(message.status)}
                                                </span>
                                            </div>
                                            {message.error_message && (
                                                <p className="text-xs text-red-500 mt-1">
                                                    {message.error_message}
                                                </p>
                                            )}
                                        </td>
                                        <td className="py-4 text-gray-600 text-sm">
                                            {message.sent_at
                                                ? new Date(message.sent_at).toLocaleString('pt-BR')
                                                : message.created_at
                                                    ? new Date(message.created_at).toLocaleString('pt-BR')
                                                    : '-'}
                                        </td>
                                        <td className="py-4">
                                            <button className="p-2 hover:bg-gray-100 rounded-lg">
                                                <MoreVertical className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <MessageCircle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                        <h3 className="font-medium text-gray-900 mb-2">Nenhuma mensagem ainda</h3>
                        <p className="text-gray-500 mb-4">
                            Envie sua primeira mensagem para os alunos
                        </p>
                        <button onClick={() => setShowSendModal(true)} className="btn-primary">
                            <Send className="w-4 h-4 mr-2" />
                            Enviar Mensagem
                        </button>
                    </div>
                )}
            </div>

            {/* Templates */}
            <div className="card">
                <h2 className="font-semibold flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5" />
                    Templates Disponíveis
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {templates?.map((template) => (
                        <div key={template.id} className="border rounded-xl p-4 hover:border-primary-300 transition-colors">
                            <div className="flex items-start justify-between mb-2">
                                <h3 className="font-medium text-gray-900">{template.name}</h3>
                                <span className="badge badge-secondary text-xs">{template.category}</span>
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-3">{template.body_text}</p>
                            <button
                                onClick={() => {
                                    setSelectedTemplate(template);
                                    setShowSendModal(true);
                                }}
                                className="btn-ghost btn-sm mt-3 w-full"
                            >
                                <Send className="w-4 h-4 mr-1" />
                                Usar Template
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Send Modal */}
            {showSendModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
                        <div className="p-6 border-b">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold">Enviar Mensagem</h2>
                                <button
                                    onClick={() => setShowSendModal(false)}
                                    className="p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
                            {/* Template Selection */}
                            {selectedTemplate ? (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="badge badge-primary">{selectedTemplate.name}</span>
                                        <button
                                            onClick={() => setSelectedTemplate(null)}
                                            className="text-gray-500 hover:text-gray-700"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <p className="text-sm text-gray-600">{selectedTemplate.body_text}</p>
                                </div>
                            ) : (
                                <div>
                                    <label className="label">Mensagem Personalizada</label>
                                    <textarea
                                        value={customMessage}
                                        onChange={(e) => setCustomMessage(e.target.value)}
                                        placeholder="Digite sua mensagem..."
                                        className="input"
                                        rows={4}
                                    />
                                </div>
                            )}

                            {/* Student Selection */}
                            <div>
                                <label className="label flex items-center justify-between">
                                    <span>Selecionar Alunos</span>
                                    <button
                                        onClick={() => {
                                            if (selectedStudents.length === students?.length) {
                                                setSelectedStudents([]);
                                            } else {
                                                setSelectedStudents(students?.map((s) => s.id) || []);
                                            }
                                        }}
                                        className="text-sm text-primary-600"
                                    >
                                        {selectedStudents.length === students?.length ? 'Desmarcar todos' : 'Selecionar todos'}
                                    </button>
                                </label>
                                <div className="border rounded-lg max-h-48 overflow-y-auto">
                                    {students?.map((student) => (
                                        <label
                                            key={student.id}
                                            className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${selectedStudents.includes(student.id) ? 'bg-primary-50' : ''
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedStudents.includes(student.id)}
                                                onChange={() => {
                                                    setSelectedStudents((prev) =>
                                                        prev.includes(student.id)
                                                            ? prev.filter((id) => id !== student.id)
                                                            : [...prev, student.id]
                                                    );
                                                }}
                                                className="rounded border-gray-300 text-primary-600"
                                            />
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-900">{student.full_name}</p>
                                                <p className="text-sm text-gray-500">{student.phone}</p>
                                            </div>
                                        </label>
                                    ))}
                                    {(!students || students.length === 0) && (
                                        <p className="p-4 text-center text-gray-500">
                                            Nenhum aluno com telefone cadastrado
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t bg-gray-50">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-500">
                                    {selectedStudents.length} aluno(s) selecionado(s)
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setShowSendModal(false)}
                                        className="btn-secondary"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={() => sendMessageMutation.mutate()}
                                        disabled={
                                            selectedStudents.length === 0 ||
                                            (!selectedTemplate && !customMessage) ||
                                            sendMessageMutation.isPending
                                        }
                                        className="btn-primary"
                                    >
                                        {sendMessageMutation.isPending ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        ) : (
                                            <Send className="w-4 h-4 mr-2" />
                                        )}
                                        Enviar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Stat Card Component
function StatCard({
    icon,
    label,
    value,
    color,
}: {
    icon: React.ReactNode;
    label: string;
    value: number;
    color: 'blue' | 'green' | 'purple' | 'red';
}) {
    const colorClasses = {
        blue: 'bg-blue-100 text-blue-600',
        green: 'bg-green-100 text-green-600',
        purple: 'bg-purple-100 text-purple-600',
        red: 'bg-red-100 text-red-600',
    };

    return (
        <div className="card flex items-center gap-4">
            <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
                {icon}
            </div>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-sm text-gray-500">{label}</p>
            </div>
        </div>
    );
}
